import { fileId } from "@inrev/common";
import * as z from "zod";

/**
 * The structure `ask_org_chart` read from a fiscal year-end statement, after uw-agent's own checks.
 * The model never writes this: the tool records it beside the answer, apps/api stores it, and the
 * panel draws it, so every entity and flag on screen is the tool's.
 *
 * `not_described` is the notes' silence, kept apart from `no` on purpose.
 */
const MAX_ENTITIES = 100;
/** Files one reading takes; uw-agent's MAX_FILES mirrors it. */
const MAX_SOURCES = 4;

const answer = z.enum(["yes", "no", "not_described"]);
export type Answer = z.infer<typeof answer>;

const RELATIONSHIPS = [
	"loanToEntity",
	"loanFromEntity",
	"arFromEntity",
	"apToEntity",
	"rentPaidToEntity",
	"entityOwnsRealEstate",
	"equipmentRentedFromEntity",
	"entityOwnsEquipment",
	"companyInvestsInEntity",
	"entityIsVendor",
	"companyWorksForEntity",
] as const;
export type Relationship = (typeof RELATIONSHIPS)[number];

const pageNumbers = z.array(z.number().int().positive()).max(50);

/** A page in one of the files the reading took, so a citation opens the right document. */
const pages = z.array(z.object({ fileId, page: z.number().int().positive() })).max(50);

/** A minority stake this entity holds in another listed entity it is not the parent of. */
export namespace Stake {
	export const schema = z.object({
		entityId: z.number().int(),
		pct: z.string().max(200).nullable(),
	});
}
export type Stake = z.infer<typeof Stake.schema>;

export namespace Entity {
	export const schema = z.object({
		id: z.number().int(),
		name: z.string().min(1).max(300),
		parentKind: z.enum(["entity", "top", "not_described"]),
		parentId: z.number().int().nullable(),
		/** How a parent was read: the notes say so, or a stated stake or control keyword implies it. */
		parentBasis: z.enum(["stated", "derived"]).nullable(),
		consolidated: answer,
		vie: answer,
		commonOwnerFullyOwned: answer,
		ownershipPct: z.string().max(200).nullable(),
		stakes: z.array(Stake.schema).max(MAX_ENTITIES),
		natureOfRelationship: z.array(z.enum(RELATIONSHIPS)).max(RELATIONSHIPS.length),
		subsequentEvent: z.enum(["acquired", "formed", "sold", "wound_down"]).nullable(),
		changeSincePrior: z.enum(["new", "changed", "unchanged"]).nullable(),
		reasoning: z.string().max(2000),
		pages,
	});
}
export type Entity = z.infer<typeof Entity.schema>;

export namespace RemovedEntity {
	export const schema = z.object({
		name: z.string().min(1).max(300),
		reasoning: z.string().max(2000),
		priorPages: pageNumbers,
	});
}
export type RemovedEntity = z.infer<typeof RemovedEntity.schema>;

const source = z.object({ fileId, fileName: z.string().max(500) });

/**
 * A file the reading took, and what it is. The panel labels a cited page with this, and an
 * underwriter weighs audited notes far above a self-reported questionnaire.
 */
const readSource = source.extend({ role: z.enum(["statement", "supporting"]) });

export const schema = z.object({
	reportingEntity: z.string().max(300),
	periodEnd: z.string().max(40).nullable(),
	priorPeriodEnd: z.string().max(40).nullable(),
	isComprehensive: z.boolean(),
	completenessNote: z.string().max(2000).nullable(),
	sources: z.array(readSource).min(1).max(MAX_SOURCES),
	priorFile: source.nullable(),
	entities: z.array(Entity.schema).max(MAX_ENTITIES),
	removedSincePrior: z.array(RemovedEntity.schema).max(MAX_ENTITIES),
});
export type OrgChart = z.infer<typeof schema>;
